/**
 * mapsLoader.js — v2.5.126 (Phase 11.2)
 * ─────────────────────────────────────────────────────────────────
 * Singleton loader for the Google Maps JavaScript library. Reasons
 * we don't just inject `<script src=…>` per consumer:
 *
 *   1. The Maps loader script can only be loaded once per page.
 *      Loading it twice triggers a noisy console warning and may
 *      break Places Autocomplete.
 *   2. Several consumers (venue address, player address, future
 *      schedule generator) all need it. The first one to mount
 *      injects; the rest await the existing promise.
 *   3. The API key is fetched once and cached in module scope.
 *
 * Usage:
 *   import { ensureMapsLoaded } from '../services/mapsLoader.js';
 *   const maps = await ensureMapsLoaded(); // resolves with window.google.maps
 *   if (!maps) { /* integration not configured, fall back *\/ }
 *
 * Resolves to:
 *   • the google.maps namespace if the lib loaded successfully
 *   • null if no key is configured, the network failed, or the
 *     script tag's onerror fires
 *
 * Callers should always handle the null case — it's the
 * graceful-degrade signal.
 */

import api from './api.js';

// Module-scope cache. The promise is shared across all consumers
// so simultaneous mounts don't trigger multiple loads.
let loadPromise = null;

// 8s cap on the script load. If the network is bad or the key
// is rejected by Google, we don't want to leave consumers
// hanging forever — they should fall back to plain text inputs.
const LOAD_TIMEOUT_MS = 8_000;

export function ensureMapsLoaded() {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    // If google.maps is already loaded (e.g. an earlier page in
    // the SPA loaded it), reuse.
    if (window.google?.maps?.places) {
      return window.google.maps;
    }

    // Fetch the API key from the backend. Returns
    // { enabled: false } when the admin hasn't configured it.
    let keyInfo;
    try {
      const { data } = await api.get('/maps/client-key');
      keyInfo = data;
    } catch (e) {
      console.warn('[maps] failed to fetch client key:', e?.message);
      return null;
    }
    if (!keyInfo?.enabled || !keyInfo?.key) {
      return null;
    }

    // Inject the script tag with the standard async loader pattern.
    // libraries=places loads Places Autocomplete + the matching
    // namespace; v=weekly is Google's recommended channel for
    // production (gets bugfixes, not breaking changes).
    return new Promise((resolve) => {
      const script = document.createElement('script');
      const params = new URLSearchParams({
        key: keyInfo.key,
        libraries: 'places',
        v: 'weekly',
        // No callback param — we listen for the `load` event
        // instead, which is more reliable across Vite/HMR.
        loading: 'async',
      });
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;

      const timer = setTimeout(() => {
        console.warn('[maps] load timeout');
        resolve(null);
      }, LOAD_TIMEOUT_MS);

      script.addEventListener('load', () => {
        clearTimeout(timer);
        if (window.google?.maps) {
          resolve(window.google.maps);
        } else {
          console.warn('[maps] script loaded but google.maps not present');
          resolve(null);
        }
      });
      script.addEventListener('error', (e) => {
        clearTimeout(timer);
        console.warn('[maps] script error:', e);
        resolve(null);
      });

      document.head.appendChild(script);
    });
  })();

  return loadPromise;
}
